import { useId, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CHEMISTRY_SYMBOLS } from '@peruselab/structure-editor/domain/symbolShortcuts';
import { Icon } from './Icon';
import { useEscapeKey } from '../hooks/useEscapeKey';

// The grid's buttons must not take focus from the text they type into.
const keepFocus = (event: SyntheticEvent) => event.preventDefault();

/**
 * #1137 follow-up (owner, 2026-10-31) -- the Symbols button: a small grid of the characters a
 * chemist writes by an arrow and in notes (°, Δ, hν, ±, →, ⇌, Greek …). Opens in place, never as a
 * dialog, so the text being edited keeps its caret; a symbol clicked is typed there.
 */
export function SymbolPicker({ onInsert }: { readonly onInsert: (symbol: string) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const gridId = useId();
  useEscapeKey(open, () => setOpen(false));
  return (
    <span className="symbol-picker">
      <button
        type="button"
        className={`icon-button ${open ? 'active' : ''}`}
        aria-label={t('symbols.button')}
        title={t('symbols.button')}
        aria-expanded={open}
        aria-controls={gridId}
        onPointerDown={keepFocus}
        onMouseDown={keepFocus}
        onClick={() => setOpen((current) => !current)}
      >
        <Icon name="specialCharacter" size="1.15rem" />
      </button>
      {open ? (
        <span id={gridId} className="symbol-picker-grid" role="group" aria-label={t('symbols.grid')}>
          {CHEMISTRY_SYMBOLS.map((symbol) => (
            <button
              key={symbol}
              type="button"
              aria-label={t('symbols.insert', { symbol })}
              title={t('symbols.insert', { symbol })}
              onPointerDown={keepFocus}
              onMouseDown={keepFocus}
              onClick={() => onInsert(symbol)}
            >
              {symbol}
            </button>
          ))}
          <small className="symbol-picker-hint">{t('symbols.hint')}</small>
        </span>
      ) : null}
    </span>
  );
}
